// ============================================================================
// raidService — thin client wrapper around the server-authoritative raid flow
// ----------------------------------------------------------------------------
// All transactional logic (boss-HP, damage, payouts, curse, audit) lives in
// functions/src/experienceEngine.ts. Damage is now applied implicitly when
// raid-eligible actions (CHECKLIST_COMPLETE / TASK_COMPLETE / FOCUS_BLOCK)
// flow through processExperienceEvent — there is no separate client damage
// path. This module just exposes the start/resolve/subscribe primitives.
// ============================================================================

import {
  doc,
  onSnapshot,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb, firebaseFunctions } from './firebase'
import { httpsCallable } from 'firebase/functions'
import { loadEngineRegistry } from './engineRegistry'
import { isNoSignInPreviewActive } from '../debug'
import type { ActiveRaidModel, RaidView } from '../types/schema'
import type { RaidCurseDoc } from '../utils/raidCurse'

export const RAID_COLLECTION = 'raids'
export const RAID_CURSE_COLLECTION = 'raid_curses'

export const getRaidDocRef = (db: Firestore, workspaceId: string) =>
  doc(db, RAID_COLLECTION, workspaceId)

export const getRaidCurseDocRef = (db: Firestore, workspaceId: string) =>
  doc(db, RAID_CURSE_COLLECTION, workspaceId)

interface CommonOptions {
  nowIso?: string
  db?: Firestore
}

export interface StartRaidInput {
  workspaceId: string
  targetElementId: string
  bossTitle: string
  isPenaltyRaid: boolean
  defaultRaidView: RaidView
  allowedRaidViews: RaidView[]
  /** From the target cluster — used by computeBossHp. */
  nestedTaskCount: number
  weightMass: number
  nestedChecklistCount: number
  enrageDeadlineTimestamp: string
  /** Credit pool the organizer funds out of the team wallet. */
  fundingCredits: number
}

export const startRaid = async (input: StartRaidInput, options: CommonOptions = {}): Promise<ActiveRaidModel> => {
  void options
  const registry = await loadEngineRegistry()
  const call = httpsCallable<Record<string, unknown>, ActiveRaidModel>(firebaseFunctions, 'startExperienceRaid')
  return (await call({ registryVersion: registry.registryVersion, input, entryId: crypto.randomUUID().replaceAll('-', '_') })).data
}

export interface ResolveRaidResult {
  outcome: 'VICTORY' | 'FAIL_NORMAL' | 'FAIL_PENALTY'
  payouts: Array<{ uid: string; userEmail: string; userDisplayName: string; share: number }>
}

export const resolveRaid = async (
  workspaceId: string,
  isVictory: boolean,
  options: CommonOptions = {},
): Promise<ResolveRaidResult> => {
  void options
  if (isNoSignInPreviewActive()) return resolveDebugRaid(workspaceId, isVictory)
  const registry = await loadEngineRegistry()
  const call = httpsCallable<Record<string, unknown>, ResolveRaidResult>(firebaseFunctions, 'resolveExperienceRaid')
  return (await call({ registryVersion: registry.registryVersion, workspaceId, isVictory, entryId: crypto.randomUUID().replaceAll('-', '_') })).data
}

export const subscribeActiveRaid = (
  workspaceId: string,
  onChange: (raid: ActiveRaidModel | null) => void,
): Unsubscribe => {
  if (isNoSignInPreviewActive()) {
    onChange(readDebugRaid(workspaceId))
    return subscribeDebugRaid(workspaceId, onChange)
  }
  return onSnapshot(
    getRaidDocRef(firestoreDb, workspaceId),
    (snapshot) => onChange(snapshot.exists() ? (snapshot.data() as ActiveRaidModel) : null),
    (error) => {
      console.warn('[raidService] subscription failed', error)
      onChange(null)
    },
  )
}

export const subscribeRaidCurse = (
  workspaceId: string,
  onChange: (curse: RaidCurseDoc | null) => void,
): Unsubscribe => {
  if (isNoSignInPreviewActive()) {
    onChange(readDebugCurse(workspaceId))
    return subscribeDebugCurse(workspaceId, onChange)
  }
  return onSnapshot(
    getRaidCurseDocRef(firestoreDb, workspaceId),
    (snapshot) => onChange(snapshot.exists() ? (snapshot.data() as RaidCurseDoc) : null),
    (error) => {
      console.warn('[raidService] curse subscription failed', error)
      onChange(null)
    },
  )
}

// ---------------------------------------------------------------------------
// Debug fixtures — allow the /?noSignIn=debug path to render every raid state
// (Forge, Active >24h, Active <6h, Curse, Victory) without a Firestore emulator.
// Setting `localStorage.debugRaid = <JSON>` seeds an ActiveRaidModel; likewise
// `localStorage.debugRaidCurse` seeds the curse doc. Both fire a storage event
// on change so subscribers pick up live edits from the console.
// ---------------------------------------------------------------------------

const DEBUG_RAID_KEY = 'debugRaid'
const DEBUG_CURSE_KEY = 'debugRaidCurse'

const readJson = <T>(key: string): T | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

const readDebugRaid = (workspaceId: string): ActiveRaidModel | null => {
  const raid = readJson<ActiveRaidModel>(DEBUG_RAID_KEY)
  if (!raid) return null
  return { ...raid, raidId: raid.raidId ?? `debug_${workspaceId}` }
}

const readDebugCurse = (workspaceId: string): RaidCurseDoc | null => {
  const curse = readJson<RaidCurseDoc>(DEBUG_CURSE_KEY)
  if (!curse) return null
  return { workspaceId, ...curse }
}

const subscribeDebugKey = <T>(
  key: string,
  workspaceId: string,
  read: (workspaceId: string) => T | null,
  onChange: (value: T | null) => void,
): Unsubscribe => {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: StorageEvent) => {
    if (event.key === key) onChange(read(workspaceId))
  }
  window.addEventListener('storage', handler)
  const customHandler = () => onChange(read(workspaceId))
  window.addEventListener(`timelining:${key}-changed`, customHandler)
  return () => {
    window.removeEventListener('storage', handler)
    window.removeEventListener(`timelining:${key}-changed`, customHandler)
  }
}

const subscribeDebugRaid = (
  workspaceId: string,
  onChange: (raid: ActiveRaidModel | null) => void,
): Unsubscribe => subscribeDebugKey(DEBUG_RAID_KEY, workspaceId, readDebugRaid, onChange)

const subscribeDebugCurse = (
  workspaceId: string,
  onChange: (curse: RaidCurseDoc | null) => void,
): Unsubscribe => subscribeDebugKey(DEBUG_CURSE_KEY, workspaceId, readDebugCurse, onChange)

/**
 * Debug-bypass path for resolveRaid. The production callable can't run without
 * Firebase Functions, so we simulate the resolution: append to history, then
 * clear the raid doc so the UI returns to the Forge state.
 */
const resolveDebugRaid = (workspaceId: string, isVictory: boolean): ResolveRaidResult => {
  const current = readDebugRaid(workspaceId)
  // History recording lives in RaidContext.resolveRaid so both prod and debug
  // share one write path — see context/RaidContext.tsx.
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(DEBUG_RAID_KEY)
    window.dispatchEvent(new Event(`timelining:${DEBUG_RAID_KEY}-changed`))
  }
  const payouts = current
    ? Object.values(current.damageLeaderboard).map((entry) => ({
        uid: entry.userEmail,
        userEmail: entry.userEmail,
        userDisplayName: entry.userDisplayName,
        share: isVictory
          ? Math.round((entry.damageDealt / Math.max(1, current.initialBossHp)) * current.allocatedCreditPool)
          : 0,
      }))
    : []
  return {
    outcome: isVictory ? 'VICTORY' : 'FAIL_NORMAL',
    payouts,
  }
}
